import { ValidationResult } from '../types.js';
import { FileSystemAdapter } from '../utils/file-system-adapter.js';
import path from 'path';

export class FileSystemValidator {
  constructor(private fs: FileSystemAdapter) {}

  /**
   * Validates file operations
   */
  async validateFileOperation(targetPath: string): Promise<ValidationResult> {
    const errors: string[] = [];

    // Check if target directory exists
    const targetDir = path.dirname(targetPath);
    if (!await this.fs.exists(targetDir)) {
      errors.push(`Target directory does not exist: ${targetDir}`);
    }

    // Check if target file already exists
    if (await this.fs.exists(targetPath)) {
      errors.push(`Target file already exists: ${targetPath}`);
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings: []
    };
  }

  /**
   * Validates file paths for move operations
   */
  async validateFilePath(sourcePath: string, targetPath: string): Promise<ValidationResult> {
    const errors: string[] = [];

    // Check if source file exists
    if (!await this.fs.exists(sourcePath)) {
      errors.push(`Source file does not exist: ${sourcePath}`);
    }

    // Check if target directory exists
    const targetDir = path.dirname(targetPath);
    if (!await this.fs.exists(targetDir)) {
      errors.push(`Target directory does not exist: ${targetDir}`);
    }

    // Check if target file already exists and is different from source
    if (sourcePath !== targetPath && await this.fs.exists(targetPath)) {
      errors.push(`Target file already exists: ${targetPath}`);
    }

    // Check if paths are absolute
    if (!path.isAbsolute(sourcePath)) {
      errors.push('Source path must be absolute');
    }
    if (!path.isAbsolute(targetPath)) {
      errors.push('Target path must be absolute');
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings: []
    };
  }

  /**
   * Validates directory structure
   */
  validateDirectoryStructure(dirPath: string): ValidationResult {
    const errors: string[] = [];

    // Check if path is absolute
    if (!path.isAbsolute(dirPath)) {
      errors.push('Directory path must be absolute');
    }

    // Check for parent directory traversal by looking at raw path components
    const parts = dirPath.split(path.sep);
    if (parts.some(part => part === '..')) {
      errors.push('Directory path cannot contain ".."');
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings: []
    };
  }

  async validateFileExists(filePath: string): Promise<ValidationResult> {
    const errors: string[] = [];
    
    if (!(await this.fs.exists(filePath))) {
      errors.push(`File does not exist: ${filePath}`);
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings: []
    };
  }

  async validateFileDoesNotExist(filePath: string): Promise<ValidationResult> {
    const errors: string[] = [];
    
    if (await this.fs.exists(filePath)) {
      errors.push(`File already exists: ${filePath}`);
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings: []
    };
  }

  async validateDirectoryExists(dirPath: string): Promise<ValidationResult> {
    const errors: string[] = [];
    
    if (!(await this.fs.exists(dirPath))) {
      errors.push(`Directory does not exist: ${dirPath}`);
    } else {
      const stats = await this.fs.stat(dirPath);
      if (!stats.isDirectory()) {
        errors.push(`Path exists but is not a directory: ${dirPath}`);
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings: []
    };
  }
} 